/* global Office, window */

/**
 * Google sign-in for the task pane.
 *
 * Google refuses to run its OAuth flow inside an embedded webview or iframe
 * (`disallowed_useragent`), and the task pane is exactly that — so the flow can't live in the
 * pane itself. `Office.context.ui.displayDialogAsync` opens a separate window that Google will
 * accept. Office requires the dialog's initial URL to be on the same domain as the add-in,
 * hence `google-auth.html` being served from our own origin (see `webpack.config.js`).
 *
 * The dialog posts the resulting Google ID token back via `messageParent`; we hand that to
 * auth-service, which verifies it. See `loginWithGoogle` in `auth.ts`.
 */

/** Office fires this when the user dismisses the dialog with the X instead of completing sign-in. */
const DIALOG_CLOSED = 12006;

export interface GoogleAuthMessage {
  idToken?: string;
  error?: string;
}

/** Thrown when the user closes the dialog without signing in — callers treat this as a no-op, not a failure. */
export class GoogleAuthCancelledError extends Error {
  constructor() {
    super('Google sign-in was cancelled.');
    this.name = 'GoogleAuthCancelledError';
  }
}

export function isGoogleAuthAvailable(): boolean {
  return typeof Office !== 'undefined' && !!Office.context?.ui?.displayDialogAsync;
}

/**
 * Opens the Google sign-in dialog and resolves with the Google ID token.
 * Rejects with `GoogleAuthCancelledError` if the user closes the dialog.
 */
export function requestGoogleIdToken(): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    if (!isGoogleAuthAvailable()) {
      reject(new Error('Google sign-in is only available inside Word.'));
      return;
    }

    const url = `${window.location.origin}/google-auth.html`;

    Office.context.ui.displayDialogAsync(
      url,
      { height: 60, width: 30, promptBeforeOpen: false },
      asyncResult => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          reject(
            new Error(asyncResult.error?.message ?? 'Could not open the Google sign-in window.'),
          );
          return;
        }

        const dialog = asyncResult.value;
        // Guards against resolving twice — closing the dialog fires DialogEventReceived even
        // after a successful message, and vice versa.
        let settled = false;

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg: unknown) => {
          if (settled) return;

          const raw = (arg as { message?: string }).message ?? '';
          let parsed: GoogleAuthMessage;
          try {
            parsed = JSON.parse(raw) as GoogleAuthMessage;
          } catch {
            parsed = { error: 'Unexpected response from the sign-in window.' };
          }

          settled = true;
          dialog.close();

          if (parsed.idToken) {
            resolve(parsed.idToken);
          } else {
            reject(new Error(parsed.error ?? 'Google sign-in failed.'));
          }
        });

        dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg: unknown) => {
          if (settled) return;
          settled = true;

          const code = (arg as { error?: number }).error;
          reject(
            code === DIALOG_CLOSED
              ? new GoogleAuthCancelledError()
              : new Error('The Google sign-in window closed unexpectedly.'),
          );
        });
      },
    );
  });
}
